import http from './http';

export interface InvestmentAdvisorInfoRec {
  id: string;
  userId: string;
  firmName?: string;
  advisorName?: string;
  specialization?: string;
  officeAddress?: string;
  officePhone?: string;
  cellPhone?: string;
  email?: string;
  website?: string;
  licenseNumber?: string;
  hourlyRate?: number;
  retainerAmount?: number;
  servicesProvided?: string;
  notes?: string;
  isLocked?: boolean;
}

class InvestmentAdvisorInfoService {
  async getAllInvestmentAdvisorInfo(targetUserId?: string): Promise<InvestmentAdvisorInfoRec[]> {
    const params = targetUserId ? `?targetUserId=${targetUserId}` : '';
    const response = await http.get(`/api/investment-advisor-info${params}`);

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to fetch investment advisor information');
    }

    return response.data.data || [];
  }

  async getInvestmentAdvisorInfo(targetUserId?: string): Promise<InvestmentAdvisorInfoRec[]> {
    return this.getAllInvestmentAdvisorInfo(targetUserId);
  }

  async createInvestmentAdvisorInfo(data: Partial<InvestmentAdvisorInfoRec>): Promise<InvestmentAdvisorInfoRec> {
    const response = await http.post('/api/investment-advisor-info', data);
    
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to save investment advisor information');
    }

    return response.data.data;
  }

  async updateInvestmentAdvisorInfo(id: string, data: Partial<InvestmentAdvisorInfoRec>): Promise<InvestmentAdvisorInfoRec> {
    const response = await http.patch(`/api/investment-advisor-info/${id}`, data);
    
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to update investment advisor information');
    }

    return response.data.data;
  }

  async deleteInvestmentAdvisorInfo(id: string): Promise<void> {
    const response = await http.delete(`/api/investment-advisor-info/${id}`);
    
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to delete investment advisor information');
    }
  }

  async lockInvestmentAdvisorInfo(id: string): Promise<void> {
    const response = await http.post(`/api/investment-advisor-info/${id}/lock`);
    
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to lock investment advisor information');
    }
  }

  async unlockInvestmentAdvisorInfo(id: string, granteeEmails: string[]): Promise<void> {
    const response = await http.post(`/api/investment-advisor-info/${id}/unlock`, { granteeEmails });
    
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to unlock investment advisor information');
    }
  }

  async getInvestmentAdvisorAccessGrants(id: string): Promise<any[]> {
    const response = await http.get(`/api/investment-advisor-info/${id}/access-grants`);
    
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to fetch access grants');
    }

    return response.data.data || [];
  }
}

export const investmentAdvisorInfoService = new InvestmentAdvisorInfoService();